import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Users, FileText, Settings, Calculator, Lock, ArrowRight, Search, Tag, FileSpreadsheet, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Users: Users,
  FileText: FileText,
  Settings: Settings,
  Calculator: Calculator,
  FileSpreadsheet: FileSpreadsheet,
  ClipboardCheck: ClipboardCheck,
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services/user`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Fehler beim Laden der Services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (service) => {
    if (service.status === 'enabled') {
      navigate(`/service/${service.id}`);
    } else {
      toast.info('Dieser Service ist für Sie nicht freigeschaltet. Kontaktieren Sie e-Matrix.');
    }
  };

  // Filter services by name or tags
  const filteredServices = services.filter(service => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const nameMatch = service.name.toLowerCase().includes(search);
    const tagMatch = service.tags?.some(tag => tag.toLowerCase().includes(search));
    const categoryMatch = service.category?.toLowerCase().includes(search);
    return nameMatch || tagMatch || categoryMatch;
  });

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0D2440' }}>
              Willkommen, {user?.name}!
            </h1>
            <div className="mt-1 flex items-center gap-2" style={{ color: '#5A7A9B' }}>
              <span>{user?.company}</span>
              {user?.customer_code && <Badge variant="outline" style={{ borderColor: '#2E5E99', color: '#2E5E99' }}>{user.customer_code}</Badge>}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#5A7A9B' }} />
          <Input
            placeholder="Services durchsuchen (Name oder Tag)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            style={{ borderColor: '#C8D8E8', background: 'white' }}
            data-testid="search-services"
          />
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse bg-white border" style={{ borderColor: '#C8D8E8' }}>
                <CardHeader>
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mt-2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-16 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredServices.length === 0 ? (
          <Card className="bg-white border" style={{ borderColor: '#C8D8E8' }}>
            <CardContent className="py-12 text-center">
              <Lock className="h-12 w-12 mx-auto mb-4" style={{ color: '#5A7A9B' }} />
              <h3 className="text-lg font-medium" style={{ color: '#0D2440' }}>
                {searchTerm ? 'Keine Services gefunden' : 'Keine Services verfügbar'}
              </h3>
              <p className="mt-2" style={{ color: '#5A7A9B' }}>
                {searchTerm 
                  ? 'Versuchen Sie einen anderen Suchbegriff' 
                  : 'Kontaktieren Sie e-Matrix, um Services freizuschalten.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => {
              const IconComponent = iconMap[service.icon] || FileText;
              const isEnabled = service.status === 'enabled';

              return (
                <Card
                  key={service.id}
                  className={`bg-white border transition-all duration-200 ${
                    isEnabled
                      ? 'service-card-enabled cursor-pointer'
                      : 'service-card-disabled'
                  }`}
                  style={{ borderColor: '#C8D8E8' }}
                  onClick={() => handleServiceClick(service)}
                  data-testid={`service-card-${service.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ background: isEnabled ? 'rgba(46, 94, 153, 0.1)' : '#E7F0FA' }}
                      >
                        <IconComponent 
                          className="h-6 w-6" 
                          style={{ color: isEnabled ? '#2E5E99' : '#5A7A9B' }} 
                        />
                      </div>
                      <Badge 
                        variant={isEnabled ? 'default' : 'secondary'}
                        style={isEnabled ? { background: 'rgba(46, 94, 153, 0.1)', color: '#2E5E99' } : {}}
                      >
                        {isEnabled ? 'Verfügbar' : 'Gesperrt'}
                      </Badge>
                    </div>
                    <CardTitle className="mt-4" style={{ color: '#0D2440' }}>{service.name}</CardTitle>
                    <CardDescription style={{ color: '#5A7A9B' }}>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Tags */}
                      {service.tags && service.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {service.tags.map((tag, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="text-xs"
                              style={{ borderColor: '#C8D8E8', color: '#5A7A9B' }}
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between pt-2">
                        <Badge variant="outline" style={{ borderColor: '#C8D8E8', color: '#5A7A9B' }}>
                          {service.category}
                        </Badge>
                        {isEnabled && (
                          <div className="flex items-center text-sm" style={{ color: '#2E5E99' }}>
                            <span>Öffnen</span>
                            <ArrowRight className="ml-1 h-4 w-4" />
                          </div>
                        )}
                        {!isEnabled && (
                          <Lock className="h-4 w-4" style={{ color: '#5A7A9B' }} />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
